import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import {
  Check as CheckIcon,
  Person as PersonIcon,
  Build as ServicesIcon,
  RateReview as ReviewIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import TeamMemberService from '../../services/teamMemberService';
import { useAppData } from '../../context/AppDataContext';
import { DEFAULT_LANGUAGE } from '../../constants/Languages';
import ManagementPageLayout, { ManagementPageCard } from '../../components/layout/ManagementPageLayout';
import TeamMemberPersonalInfo, { EMAIL_CHECK_STATUS } from '../../components/team-member-registration/TeamMemberPersonalInfo';
import TeamMemberRolePermissions from '../../components/team-member-registration/TeamMemberRolePermissions';
import TeamMemberServices from '../../components/team-member-registration/TeamMemberServices';
import TeamMemberReview from '../../components/team-member-registration/TeamMemberReview';
import AssignExistingTeamMemberConfirmDialog from '../../components/team-member-registration/AssignExistingTeamMemberConfirmDialog';
import TeamMemberBillingConfirmDialog from '../../components/team-member-registration/TeamMemberBillingConfirmDialog';
import WizardStepNavigation from '../../components/common/WizardStepNavigation';
import StoreService from '../../services/storeService';
import { DEFAULT_CURRENCY } from '../../utils/currencyUtils';
import { getFunctionalItem, setFunctionalItem } from '../../utils/functionalStorage';
import { BRAND } from '../../theme/brandTokens';

const TEAM_MEMBER_REGISTRATION_STORAGE_KEY = 'salovare_teamMemberRegistration_temp';

// Global variable to track useEffect executions
const useEffectExecutionTracker = new Map();

const TeamMemberRegistrationPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { storeId } = useParams();
  const { currentStore } = useAppData();

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneCountryCode: '+351', // Default to Portugal
    phoneNumber: '',
    jobTitle: '',
    genre: '',
    birthDate: null,
    language: DEFAULT_LANGUAGE,
    roleId: null,
    additionalPermissions: [],
    services: [],
    acceptsCustomerBookings: true
  });

  const [services, setServices] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailCheckStatus, setEmailCheckStatus] = useState(EMAIL_CHECK_STATUS.IDLE);
  const [existingMemberConfirmed, setExistingMemberConfirmed] = useState(false);
  const [existingMemberName, setExistingMemberName] = useState('');
  const [showExistingMemberConfirm, setShowExistingMemberConfirm] = useState(false);
  const [existingMemberBillingImpact, setExistingMemberBillingImpact] = useState(null);
  const [billingImpact, setBillingImpact] = useState(null);
  const [billingImpactLoaded, setBillingImpactLoaded] = useState(false);
  const [loadingReview, setLoadingReview] = useState(false);
  const [showBillingConfirm, setShowBillingConfirm] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const isCompletingRegistration = useRef(false);
  const hasLoadedFromStorage = useRef(false);
  const servicesLoadingRef = useRef(false);
  const billingImpactLoadedRef = useRef(false);
  const billingImpactPromiseRef = useRef(null);
  const billingImpactRequestRef = useRef(0);
  const billingProjectionRef = useRef(true);

  // Local storage functions
  const saveToLocalStorage = (data) => {
    try {
      setFunctionalItem(TEAM_MEMBER_REGISTRATION_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save team member registration data to localStorage:', error);
    }
  };

  const loadFromLocalStorage = () => {
    try {
      const saved = getFunctionalItem(TEAM_MEMBER_REGISTRATION_STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.warn('Failed to load team member registration data from localStorage:', error);
      return null;
    }
  };

  const clearLocalStorage = () => {
    try {
      localStorage.removeItem(TEAM_MEMBER_REGISTRATION_STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear team member registration data from localStorage:', error);
    }
  };

  // Load data from localStorage on component mount
  useEffect(() => {
    const savedData = loadFromLocalStorage();
    if (savedData && savedData.formData) {
      // Convert birthDate string back to Date object if it exists
      const formData = { ...savedData.formData };
      if (formData.birthDate && typeof formData.birthDate === 'string') {
        formData.birthDate = new Date(formData.birthDate);
      }
      formData.acceptsCustomerBookings = formData.acceptsCustomerBookings !== false;
      setFormData(formData);
      setActiveStep(savedData.activeStep || 0);
    }
    hasLoadedFromStorage.current = true;
  }, []);

  // Save data to localStorage whenever formData or activeStep changes
  useEffect(() => {
    if (hasLoadedFromStorage.current) {
      const dataToSave = {
        formData,
        activeStep,
        timestamp: Date.now()
      };
      saveToLocalStorage(dataToSave);
    }
  }, [formData, activeStep]);

  useEffect(() => {
    billingImpactRequestRef.current += 1;
    billingImpactLoadedRef.current = false;
    billingImpactPromiseRef.current = null;
    setBillingImpact(null);
    setBillingImpactLoaded(false);
  }, [storeId, formData.acceptsCustomerBookings]);

  const ensureBillingImpactLoaded = useCallback(async () => {
    const projectedAdditionalMember = formData.acceptsCustomerBookings !== false;
    if (billingImpactLoadedRef.current && billingProjectionRef.current === projectedAdditionalMember) {
      return billingImpact;
    }

    if (billingImpactPromiseRef.current) {
      return billingImpactPromiseRef.current;
    }

    const requestId = billingImpactRequestRef.current;
    const promise = StoreService.getTeamMemberBillingImpact(storeId, {
      projectedAdditionalMember,
    })
      .then((impact) => {
        if (billingImpactRequestRef.current !== requestId) {
          return null;
        }

        billingProjectionRef.current = projectedAdditionalMember;
        setBillingImpact(impact);
        setBillingImpactLoaded(true);
        billingImpactLoadedRef.current = true;
        return impact;
      })
      .catch((error) => {
        if (billingImpactRequestRef.current !== requestId) {
          return null;
        }

        setBillingImpact(null);
        setBillingImpactLoaded(true);
        billingImpactLoadedRef.current = true;
        throw error;
      })
      .finally(() => {
        if (billingImpactPromiseRef.current === promise) {
          billingImpactPromiseRef.current = null;
        }
      });

    billingImpactPromiseRef.current = promise;
    return promise;
  }, [billingImpact, formData.acceptsCustomerBookings, storeId]);

  useEffect(() => {
    if (activeStep !== 3 || !storeId || billingImpactLoadedRef.current) {
      return undefined;
    }

    let cancelled = false;

    const loadForRestoredReviewStep = async () => {
      setLoadingReview(true);
      try {
        await ensureBillingImpactLoaded();
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load team member billing impact:', error);
        }
      } finally {
        if (!cancelled) {
          setLoadingReview(false);
        }
      }
    };

    loadForRestoredReviewStep();

    return () => {
      cancelled = true;
    };
  }, [activeStep, ensureBillingImpactLoaded, storeId]);

  // Cleanup localStorage when component unmounts
  useEffect(() => {
    return () => {
      if (!isCompletingRegistration.current) {
        clearLocalStorage();
      }

      servicesLoadingRef.current = false;
    };
  }, []);

  // Load services for the current store
  useEffect(() => {
    const loadServices = async () => {
      if (!storeId || servicesLoadingRef.current || isCompletingRegistration.current) return;

      const executionKey = `loadServices-${storeId}`;

      if (useEffectExecutionTracker.has(executionKey)) {
        return;
      }

      useEffectExecutionTracker.set(executionKey, true);

      try {
        servicesLoadingRef.current = true;
        const servicesData = await TeamMemberService.getServicesByStore(storeId);
        setServices(servicesData);
      } catch (error) {
        console.error('Failed to load services:', error);
        useEffectExecutionTracker.delete(executionKey);
        setSnackbar({
          open: true,
          message: t('teamMembers.registration.error.loadingServices'),
          severity: 'error'
        });
      } finally {
        servicesLoadingRef.current = false;
      }
    };

    loadServices();
  }, [storeId, t]);

  const steps = [
    {
      label: t('teamMembers.registration.steps.step1.title'),
      description: t('teamMembers.registration.steps.step1.description'),
      icon: <PersonIcon />
    },
    {
      label: t('teamMembers.registration.steps.step2.title'),
      description: t('teamMembers.registration.steps.step2.description'),
      icon: <LockIcon />
    },
    {
      label: t('teamMembers.registration.steps.step3.title'),
      description: t('teamMembers.registration.steps.step3.description'),
      icon: <ServicesIcon />
    },
    {
      label: t('teamMembers.registration.steps.step4.title'),
      description: t('teamMembers.registration.steps.step4.description'),
      icon: <ReviewIcon />
    }
  ];

  const isExistingMember = emailCheckStatus === EMAIL_CHECK_STATUS.EXISTING && existingMemberConfirmed;
  const fieldsEnabled = emailCheckStatus === EMAIL_CHECK_STATUS.AVAILABLE
    || (emailCheckStatus === EMAIL_CHECK_STATUS.EXISTING && existingMemberConfirmed);

  const handleEmailChange = (email) => {
    setFormData((prev) => ({ ...prev, email }));
    setEmailCheckStatus(EMAIL_CHECK_STATUS.IDLE);
    setExistingMemberConfirmed(false);
    setExistingMemberName('');
    setShowExistingMemberConfirm(false);
    setErrors((prev) => {
      const nextErrors = { ...prev };
      delete nextErrors.email;
      return nextErrors;
    });
  };

  const handleCheckEmail = async () => {
    const email = formData.email.trim();
    const newErrors = {};

    if (!email) {
      newErrors.email = t('teamMembers.registration.validation.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = t('teamMembers.registration.validation.emailInvalid');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setCheckingEmail(true);
    setEmailCheckStatus(EMAIL_CHECK_STATUS.CHECKING);

    try {
      const result = await TeamMemberService.checkTeamMemberEmail(email, storeId);

      if (!result.exists) {
        setEmailCheckStatus(EMAIL_CHECK_STATUS.AVAILABLE);
        setExistingMemberConfirmed(false);
        setExistingMemberName('');
        return;
      }

      if (result.alreadyAssignedToStore) {
        setEmailCheckStatus(EMAIL_CHECK_STATUS.ALREADY_IN_STORE);
        setExistingMemberConfirmed(false);
        setExistingMemberName(result.name || '');
        return;
      }

      setExistingMemberName(result.name || '');
      setFormData((prev) => ({ ...prev, name: result.name || prev.name }));
      setEmailCheckStatus(EMAIL_CHECK_STATUS.EXISTING);
      try {
        const impact = await StoreService.getTeamMemberBillingImpact(storeId, {
          projectedAdditionalMember: formData.acceptsCustomerBookings !== false,
        });
        setExistingMemberBillingImpact(impact);
      } catch (error) {
        console.error('Failed to load team member billing impact:', error);
        setExistingMemberBillingImpact(null);
      }
      setShowExistingMemberConfirm(true);
    } catch (error) {
      console.error('Failed to check team member email:', error);
      setEmailCheckStatus(EMAIL_CHECK_STATUS.ERROR);
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleConfirmExistingMember = () => {
    setExistingMemberConfirmed(true);
    setShowExistingMemberConfirm(false);
  };

  const handleNext = async () => {
    if (!validateStep(activeStep)) {
      return;
    }

    if (activeStep === 2) {
      setLoadingReview(true);
      try {
        await ensureBillingImpactLoaded();
        setActiveStep(3);
      } catch (error) {
        console.error('Failed to load team member billing impact:', error);
        setActiveStep(3);
      } finally {
        setLoadingReview(false);
      }
      return;
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) {
      return;
    }

    const willIncurAdditionalCharge = Boolean(
      billingImpact?.willIncurAdditionalCharge ?? billingImpact?.WillIncurAdditionalCharge
    );

    if (willIncurAdditionalCharge) {
      setShowBillingConfirm(true);
      return;
    }

    await performCreateTeamMember();
  };

  const performCreateTeamMember = async () => {
    setLoading(true);
    isCompletingRegistration.current = true;

    try {
        // Convert Date object to yyyy-mm-dd for backend
        let birthDateForBackend = null;
        if (formData.birthDate) {
          const year = formData.birthDate.getFullYear();
          const month = String(formData.birthDate.getMonth() + 1).padStart(2, '0');
          const day = String(formData.birthDate.getDate()).padStart(2, '0');
          birthDateForBackend = `${year}-${month}-${day}`;
        }

        const storePayload = {
          storeId: storeId,
          roleId: formData.roleId || null,
          jobTitle: formData.jobTitle?.trim() || null,
          additionalPermissions: formData.additionalPermissions || [],
          acceptsCustomerBookings: formData.acceptsCustomerBookings !== false
        };

        const teamMemberData = isExistingMember
          ? {
            name: existingMemberName || formData.name,
            email: formData.email,
            store: storePayload,
            services: (formData.services || []).map(service => ({
              serviceId: service.serviceId,
              customPrice: service.customPrice || null
            }))
          }
          : {
            name: formData.name,
            email: formData.email,
            phone: {
              countryCode: formData.phoneCountryCode,
              number: formData.phoneNumber
            },
            genre: formData.genre || null,
            ...(birthDateForBackend && { birthDate: birthDateForBackend }),
            services: (formData.services || []).map(service => ({
              serviceId: service.serviceId,
              customPrice: service.customPrice || null
            })),
            store: storePayload,
            language: formData.language
          };

        await TeamMemberService.createTeamMember(teamMemberData);

        clearLocalStorage();

        setSnackbar({
          open: true,
          message: t(isExistingMember
            ? 'teamMembers.registration.success.assigned'
            : 'teamMembers.registration.success.created'),
          severity: 'success'
        });

        // Navigate back to team members page after a short delay
        setTimeout(() => {
          navigate(`/management/stores/${storeId}/team-members`);
        }, 2000);

      } catch (error) {
        console.error('Failed to create team member:', error);
        const errorCode = error?.response?.data?.code;
        setSnackbar({
          open: true,
          message: errorCode === 'TeamMemberBillingSyncFailed'
            ? t('teamMembers.billing.syncFailed')
            : t('teamMembers.registration.error.creation'),
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
  };

  const handleBillingConfirm = async () => {
    setShowBillingConfirm(false);
    await performCreateTeamMember();
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0: // Personal Info (including Phone)
        if (!formData.email.trim()) {
          newErrors.email = t('teamMembers.registration.validation.emailRequired');
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
          newErrors.email = t('teamMembers.registration.validation.emailInvalid');
        } else if (emailCheckStatus === EMAIL_CHECK_STATUS.IDLE || emailCheckStatus === EMAIL_CHECK_STATUS.CHECKING) {
          newErrors.email = t('teamMembers.registration.validation.emailCheckRequired');
        } else if (emailCheckStatus === EMAIL_CHECK_STATUS.ERROR) {
          newErrors.email = t('teamMembers.registration.emailCheck.error');
        } else if (emailCheckStatus === EMAIL_CHECK_STATUS.ALREADY_IN_STORE) {
          newErrors.email = t('teamMembers.registration.validation.alreadyInStore');
        } else if (emailCheckStatus === EMAIL_CHECK_STATUS.EXISTING && !existingMemberConfirmed) {
          newErrors.email = t('teamMembers.registration.validation.existingMemberConfirmationRequired');
        } else if (emailCheckStatus === EMAIL_CHECK_STATUS.AVAILABLE) {
          if (!formData.name.trim()) {
            newErrors.name = t('teamMembers.registration.validation.nameRequired');
          }
          if (!formData.phoneNumber.trim()) {
            newErrors.phoneNumber = t('teamMembers.registration.validation.phoneRequired');
          }
        }
        break;

      case 1: // Role & Permissions (no validation needed - all optional)
        break;

      case 2: // Services
        // No validation needed - team members can have no services assigned
        break;

      case 3: // Review
        // No validation needed for review step
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));

    // Clear errors for fields that are being updated
    setErrors(prev => {
      const newErrors = { ...prev };
      Object.keys(updates).forEach(key => {
        if (updates[key] && updates[key].toString().trim()) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const isNextDisabled = () => {
    if (loading || checkingEmail || loadingReview) {
      return true;
    }

    if (activeStep !== 0) {
      return false;
    }

    if (emailCheckStatus === EMAIL_CHECK_STATUS.IDLE
      || emailCheckStatus === EMAIL_CHECK_STATUS.CHECKING
      || emailCheckStatus === EMAIL_CHECK_STATUS.ERROR
      || emailCheckStatus === EMAIL_CHECK_STATUS.ALREADY_IN_STORE) {
      return true;
    }

    if (emailCheckStatus === EMAIL_CHECK_STATUS.EXISTING) {
      return !existingMemberConfirmed;
    }

    if (emailCheckStatus === EMAIL_CHECK_STATUS.AVAILABLE) {
      return !formData.name.trim() || !formData.phoneNumber.trim();
    }

    return true;
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <TeamMemberPersonalInfo
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
            emailCheckStatus={emailCheckStatus}
            checkingEmail={checkingEmail}
            onCheckEmail={handleCheckEmail}
            onEmailChange={handleEmailChange}
            isExistingMember={isExistingMember}
            existingMemberName={existingMemberName}
            fieldsEnabled={fieldsEnabled}
          />
        );
      case 1:
        return <TeamMemberRolePermissions formData={formData} updateFormData={updateFormData} errors={errors} storeId={storeId} i18n={i18n} />;
      case 2:
        return <TeamMemberServices formData={formData} updateFormData={updateFormData} services={services} errors={errors} currency={currentStore?.currency || DEFAULT_CURRENCY} />;
      case 3:
        if (!billingImpactLoaded || loadingReview) {
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          );
        }

        return (
          <TeamMemberReview
            formData={formData}
            currentStore={currentStore}
            services={services}
            isExistingMember={isExistingMember}
            existingMemberName={existingMemberName}
            billingImpact={billingImpact}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ManagementPageLayout
      id="team-member-registration-page"
      containerId="team-member-registration-header"
      titleId="team-member-registration-title"
      subtitleId="team-member-registration-subtitle"
      pageTitle={t('teamMembers.registration.title')}
      pageSubtitle={t('teamMembers.registration.subtitle')}
      contentId="team-member-registration-main-content"
      contentSx={{
        maxWidth: { lg: '960px' },
        width: { xs: '100%', lg: '960px' }
      }}
    >
      <ManagementPageCard id="team-member-registration-card">
          {/* Custom Stepper */}
          <Box id="team-member-registration-stepper-container" sx={{
            backgroundColor: BRAND.surface,
            borderBottom: `1px solid ${BRAND.line}`,
            p: 4
          }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'stretch', // This will make all items stretch to the same height
              position: 'relative'
            }}>
              {steps.map((step, index) => (
                <Box key={index} sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: 1,
                  position: 'relative',
                  overflow: 'visible',
                  justifyContent: 'flex-start' // Align content to top
                }}>
                  {/* Step Circle */}
                  <Box
                    id={`team-member-registration-step-icon-${index + 1}`}
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      background: activeStep >= index ? BRAND.accent : BRAND.surface,
                      color: activeStep >= index ? BRAND.onAccent : BRAND.textSubtle,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.4rem',
                      fontWeight: 'bold',
                      border: `3px solid ${activeStep >= index ? BRAND.accent : BRAND.line}`,
                      boxShadow: 'none',
                      transition: 'all 0.3s ease',
                      zIndex: 2,
                      position: 'relative'
                    }}
                  >
                    {activeStep > index ? <CheckIcon /> : step.icon}
                  </Box>

                  {/* Step Label */}
                  <Typography
                    id={`team-member-registration-step-label-${index + 1}`}
                    sx={{
                      color: activeStep >= index ? BRAND.text : BRAND.textMuted,
                      fontWeight: activeStep >= index ? 700 : 500,
                      fontSize: '0.9rem',
                      mt: 1.5,
                      textAlign: 'center',
                      display: { xs: 'none', sm: 'block' },
                      lineHeight: '1.25rem',
                      width: '100%',
                      flex: 1 // This will make the label take up remaining space
                    }}
                  >
                    {step.label}
                  </Typography>

                  {/* Connector Line */}
                  {index < steps.length - 1 && (
                    <Box sx={{
                      position: 'absolute',
                      top: '25px',
                      left: 'calc(50% + 25px)',
                      right: 'calc(-50% + 25px)',
                      height: '3px',
                      background: activeStep > index ? BRAND.accent : BRAND.line,
                      zIndex: 1
                    }} />
                  )}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Step Content */}
          <Box id="team-member-registration-step-content" sx={{ p: { xs: 1, sm: 4, md: 6 } }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                id={`team-member-registration-step-${activeStep + 1}-content`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {renderStepContent(activeStep)}
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <WizardStepNavigation
              id="team-member-registration-navigation"
              backId="team-member-registration-back-button"
              nextId={activeStep === steps.length - 1 ? 'team-member-registration-submit-button' : 'team-member-registration-next-button'}
              backLabel={t('common.back')}
              nextLabel={activeStep === steps.length - 1
                ? (isExistingMember ? t('teamMembers.registration.assign') : t('teamMembers.registration.create'))
                : t('common.next')}
              onBack={handleBack}
              onNext={activeStep === steps.length - 1 ? handleSubmit : handleNext}
              backDisabled={activeStep === 0}
              nextDisabled={isNextDisabled()}
              isLastStep={activeStep === steps.length - 1}
              nextLoading={activeStep === steps.length - 1 ? loading : loadingReview}
            />
          </Box>
        </ManagementPageCard>

      <AssignExistingTeamMemberConfirmDialog
        open={showExistingMemberConfirm}
        onClose={() => setShowExistingMemberConfirm(false)}
        onConfirm={handleConfirmExistingMember}
        memberName={existingMemberName}
        email={formData.email}
        billingImpact={existingMemberBillingImpact}
      />

      <TeamMemberBillingConfirmDialog
        open={showBillingConfirm}
        onClose={() => setShowBillingConfirm(false)}
        onConfirm={handleBillingConfirm}
        billingImpact={billingImpact}
        loading={loading}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ManagementPageLayout>
  );
};


export default TeamMemberRegistrationPage;